<div class="mt-8">
  <div class="flex items-center justify-between mb-4 flex-wrap gap-4">
    <h2 class="text-foreground text-xl font-bold">Hydration Records</h2>

    <!-- Single Date Picker replacing range -->
    <p-calendar
      [(ngModel)]="selectedDate"
      selectionMode="single"
      [readonlyInput]="true"
      [showClear]="true"
      placeholder="Select Date Filter"
      (onSelect)="applyDateFilter()"
      (onClear)="applyDateFilter()"
      styleClass="border border-gray-200 dark:border-foreground-tertiary rounded-xl bg-white dark:bg-card text-[13px] font-medium text-gray-700 dark:text-gray-300 shadow-sm"
      inputStyleClass="px-3 py-1.5 w-[160px] border-none shadow-none text-xs bg-transparent focus:ring-0"
    >
    </p-calendar>
  </div>

  <div class="flex flex-col gap-4">
    <!-- Empty State -->
    <div
      *ngIf="filteredHistory.length === 0"
      class="flex flex-col items-center justify-center p-12 bg-gray-50 dark:bg-card/50 rounded-[24px] border border-dashed border-gray-200 dark:border-foreground-tertiary"
    >
      <i class="pi pi-calendar-times text-4xl text-gray-300 mb-3"></i>
      <h3 class="text-gray-600 dark:text-gray-400 font-medium">
        No hydration history found for this date.
      </h3>
    </div>
    <!-- List Items Loop using filteredHistory -->
    <div
      *ngFor="let item of filteredHistory"
      class="bg-card rounded-[24px] p-4 px-5 border border-gray-100 dark:border-foreground-tertiary shadow-sm flex items-center justify-between transition-shadow"
    >
      <div class="flex items-center gap-5">
        <!-- Radial Progress Avatar -->
        <div class="relative w-[60px] h-[60px] flex items-center justify-center">
          <!-- Custom SVG Circle Progress -->
          <svg class="w-full h-full transform -rotate-90 absolute" viewBox="0 0 36 36">
            <!-- Background Circle -->
            <path
              class="text-gray-100 dark:text-gray-800"
              stroke-width="3"
              stroke="currentColor"
              fill="none"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
            <!-- Foreground Circle -->
            <path
              class="text-[#3B82F6]"
              stroke-dasharray="100, 100"
              [attr.stroke-dasharray]="getProgressComplete(item.value, item.goal) + ', 100'"
              stroke-width="3"
              stroke-linecap="round"
              stroke="currentColor"
              fill="none"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
          </svg>

          <div
            class="w-[30px] h-[30px] rounded-full bg-[#3B82F6] flex items-center justify-center relative z-10"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="white"
              stroke="white"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z"
              ></path>
            </svg>
          </div>
        </div>

        <!-- Content Payload -->
        <div class="flex flex-col">
          <span class="text-[17px] font-bold text-foreground mb-0.5">{{
            getDisplayLabel(item.date)
          }}</span>
          <div class="flex items-baseline gap-1 mb-1">
            <span class="text-[15px] font-medium text-gray-500"
              >{{ formatLiters(item.value) }} L
              <span class="text-[14px]">/ {{ formatLiters(item.goal) }} L</span></span
            >
          </div>

          <div class="flex items-center gap-2 text-[11px] font-bold text-gray-500">
            <span>{{ getProgressComplete(item.value, item.goal) }}% Complete</span>
            <span class="w-[3px] h-[3px] bg-gray-300 rounded-full"></span>

            <!-- Status Logic -->
            <span *ngIf="item.value >= item.goal" class="text-emerald-500 flex items-center gap-1">
              <i class="pi pi-check-circle" style="font-size: 0.7rem"></i> Goal Achieved
            </span>
            <span *ngIf="item.value < item.goal && item.value > 0" class="text-gray-500">
              {{ formatLiters(item.goal - item.value) }} L to reach today's goal
            </span>
            <span *ngIf="item.value === 0" class="text-gray-400"> Below Goal </span>
          </div>
        </div>
      </div>

      <!-- Edit Button -->
      <button
        [disabled]="!canEdit(item.date)"
        (click)="openDetails(item)"
        class="w-10 h-10 rounded-full flex items-center justify-center transition-colors disabled:opacity-30"
        [ngClass]="
          canEdit(item.date)
            ? 'bg-[#F2F6FE] hover:bg-[#e4ecfb] text-[#1e293b] cursor-pointer'
            : 'bg-gray-50 text-gray-400 cursor-not-allowed'
        "
      >
        <i class="pi pi-pencil font-bold"></i>
      </button>
    </div>
  </div>
</div>

<!-- Edit Log Modal precisely mimicking Dart Layout -->
<p-dialog
  [(visible)]="displayModal"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [showHeader]="true"
  header="Hydration Log"
  [dismissableMask]="true"
  styleClass="rounded-t-[30px] rounded-b-[30px] overflow-hidden max-w-[440px] w-[95vw] shadow-2xl dark:border-foreground-tertiary m-0 bg-white dark:bg-card"
>
  <div *ngIf="selectedRow" class="p-2 sm:p-5 relative pb-2 mt-2">
    <!-- Drag Handle Placeholder from Dart -->

    <!-- Main Content Row -->
    <div class="flex items-center justify-between">
      <!-- Left: Stats -->
      <div class="flex-1 flex flex-col pr-2">
        <div class="flex items-center gap-2 mb-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#262626"
            class="dark:stroke-white"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z"
            ></path>
          </svg>
          <span class="text-[18px] font-semibold text-blue-300 dark:text-gray-200">Water</span>
        </div>

        <!-- Intake Display -->
        <div class="flex items-baseline mb-2 leading-none">
          <span class="text-[40px] font-extrabold text-[#111827] dark:text-white tracking-tight">{{
            formatLiters(stagedValue)
          }}</span>
          <span
            class="text-[28px] font-extrabold text-[#111827] dark:text-white tracking-tight ml-1"
          >
            / {{ formatLiters(selectedRow.goal) }}L</span
          >
        </div>

        <!-- Percentage Display -->
        <div class="mb-3 flex items-center gap-1 text-[14px]">
          <span class="text-[16px] font-bold text-[#10B981]">{{ percentage }}%</span>
          <span class="font-semibold text-[#111827] dark:text-gray-200">of Daily water intake</span>
        </div>

        <!-- Date Chip -->
        <div
          class="inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 py-1.5 rounded-[20px] text-[12px] font-semibold text-[#111827] dark:text-gray-300 w-max mb-3"
        >
          <i class="pi pi-calendar" style="font-size: 0.8rem; color: #64748b"></i>
          <span>{{ getDisplayLabel(selectedRow.date) }} - {{ selectedRow.date }}</span>
        </div>

        <!-- Increment Step Dropdown -->
        <div>
          <span
            class="block text-[10px] font-extrabold text-gray-400 uppercase tracking-widest mb-1.5"
            >Add / Remove Step</span
          >
          <p-dropdown
            [options]="intakeOptions"
            [(ngModel)]="selectedIntake"
            optionLabel="label"
            optionValue="value"
            styleClass="w-[130px] h-[34px] flex items-center text-[13px] font-bold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm rounded-xl"
            panelStyleClass="text-xs font-medium"
          ></p-dropdown>
        </div>
      </div>

      <!-- Right: Glass + Buttons side by side matching Dart -->
      <div class="flex items-center mt-4">
        <!-- Glass Graphic -->
        <div class="relative w-[90px] h-[150px]">
          <svg viewBox="0 0 120 200" fill="none" class="absolute inset-0 w-full h-full">
            <!-- Back lip -->
            <ellipse cx="60" cy="15" rx="55" ry="10" fill="#E2E8F0" />
            <!-- Outer Body -->
            <path
              d="M5 15 L20 180 C20 190 30 195 60 195 C90 195 100 190 100 180 L115 15"
              fill="#F8FAFC"
              stroke="#CBD5E1"
              stroke-width="4"
              stroke-linejoin="round"
            />

            <path d="M12 45 L108 45" stroke="#CBD5E1" stroke-width="4" />
            <path d="M16 100 L104 100" stroke="#CBD5E1" stroke-width="4" />

            <!-- Top Cover -->
            <ellipse cx="60" cy="15" rx="55" ry="10" fill="#94A3B8" />
          </svg>

          <!-- Fluid Fill inside the tumbler -->
          <div
            class="absolute bottom-[20px] left-[15px] right-[15px] h-[160px] flex flex-col-reverse z-10 overflow-hidden"
            style="clip-path: polygon(0 0, 100% 0, 85% 100%, 15% 100%)"
          >
            <div
              class="w-full bg-[#3B82F6] transition-all duration-300 ease-out flex-shrink-0"
              [style.height]="percentage * 0.65 + '%'"
            ></div>
          </div>
        </div>

        <!-- Buttons vertically stacked right strictly beside the glass -->
        <div class="flex flex-col items-center justify-center ml-4 mt-2 h-full gap-8">
          <button
            (click)="increment()"
            [disabled]="updating"
            [class.opacity-50]="updating"
            class="w-[36px] h-[36px] rounded-full bg-[#F3F6FF] flex justify-center items-center text-[#3B82F6] hover:bg-[#e4ecfb] transition-colors"
          >
            <i class="pi pi-plus" style="font-size: 0.9rem; font-weight: bold"></i>
          </button>
          <button
            (click)="decrement()"
            [disabled]="updating || stagedValue === 0"
            [class.opacity-50]="updating || stagedValue === 0"
            class="w-[36px] h-[36px] rounded-full bg-[#F9FAFB] flex justify-center items-center text-gray-400 hover:bg-gray-100 border border-gray-100 transition-colors"
          >
            <i class="pi pi-minus" style="font-size: 0.9rem; font-weight: bold"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Info Box -->
    <div class="mt-3 bg-blue-50/50 border border-blue-100 rounded-[16px] p-4 flex gap-3">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        class="text-[#3B82F6] mt-0.5 flex-shrink-0"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="12" cy="12" r="5"></circle>
        <line x1="12" y1="1" x2="12" y2="3"></line>
        <line x1="12" y1="21" x2="12" y2="23"></line>
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
        <line x1="1" y1="12" x2="3" y2="12"></line>
        <line x1="21" y1="12" x2="23" y2="12"></line>
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
      </svg>
      <p class="text-[13.5px] text-[#374151] font-medium leading-[1.4]">
        You have completed {{ percentage }}% of your hydration goal
        {{ getDisplayLabel(selectedRow.date).toLowerCase() }}. Try drinking water regularly to reach
        your daily target.
      </p>
    </div>

    <!-- Single Action API Update Trigger (Requested separately) -->
    <button
      (click)="saveUpdates()"
      [disabled]="updating"
      class="w-full mt-3 bg-[#3B82F6] hover:bg-blue-600 transition-colors text-white font-bold text-[16px] py-4 rounded-[16px] flex items-center justify-center"
    >
      <span *ngIf="!updating">Update and Close</span>
      <i *ngIf="updating" class="pi pi-spin pi-spinner"></i>
    </button>
  </div>
</p-dialog>
